import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { AuthProvider } from "@marekl/adminer";
import { RequireAuth, RouteError } from "@marekl/adminer/shell";
import { RequireGuest } from "./auth/RequireGuest";
import { AppLayout } from "./shell/AppLayout";
import { LoginPage } from "./pages/LoginPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { SettingsPage } from "./pages/SettingsPage";
import { CompaniesPage } from "./pages/CompaniesPage";
import { CompanySettingsPage } from "./pages/CompanySettingsPage";
import { ContactEditPage, ContactsPage } from "./pages/ContactsPage";
import { ProductEditPage, ProductsPage } from "./pages/ProductsPage";
import { DocumentsPage } from "./pages/DocumentsPage";
import { DocumentEditorPage } from "./pages/DocumentEditorPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DocumentDetailPage } from "./pages/DocumentDetailPage";
import { CompanyScope, StartRedirect } from "./lib/company";
import { DOC_TYPE_SLUG, type DocumentType } from "./lib/queries/documents";

/**
 * Data router (createBrowserRouter + RouterProvider) from day one, so that
 *   - `useUnsavedChangesGuard` works for in-app navigation (useBlocker is a
 *     data-router API) — the invoice editor depends on it, and
 *   - route modals use the nesting form: a detail route is a child of its
 *     list, so the list stays mounted under the dialog by construction.
 * (The POC web-app started on <BrowserRouter> and cannot use the guard.)
 */
/**
 * The library's route error screen, in this app's words. It hangs on the root
 * routes, which replaces <AdminerProvider> with it — so the copy is passed
 * rather than read through the translator.
 */
function RouteErrorPage() {
  return (
    <RouteError
      homeTo="/"
      title="Něco se pokazilo"
      subtitle="Tuto stránku se nepodařilo zobrazit. Zkuste ji načíst znovu; pokud se to opakuje, dejte nám vědět, co jste právě dělali."
      homeLabel="Zpět na přehled"
      reloadLabel="Načíst znovu"
    />
  );
}

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <RequireGuest>
        <LoginPage />
      </RequireGuest>
    ),
    errorElement: <RouteErrorPage />,
  },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    // A data router catches render errors inside a route and looks for that
    // route's errorElement — the <ErrorBoundary> in main.tsx sits above
    // RouterProvider and never sees them.
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <StartRedirect /> },
      { path: "/dashboard", element: <StartRedirect /> },
      { path: "/audit-log", element: <AuditLogPage /> },
      { path: "/settings", element: <SettingsPage /> },
      { path: "/companies", element: <CompaniesPage /> },
      {
        // Everything inside one company — the tenant — lives under its id.
        path: "/c/:companyId",
        element: <CompanyScope />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <DashboardPage /> },
          ...(Object.entries(DOC_TYPE_SLUG) as [DocumentType, string][]).map(([type, slug]) => ({
            path: slug,
            element: <DocumentsPage key={type} type={type} />,
          })),
          { path: "documents", element: <Navigate to="../invoices" replace /> },
          { path: "documents/new", element: <DocumentEditorPage /> },
          { path: "documents/:documentId", element: <DocumentDetailPage /> },
          { path: "documents/:documentId/edit", element: <DocumentEditorPage /> },
          { path: "contacts", element: <ContactsPage /> },
          { path: "contacts/:contactId", element: <ContactEditPage /> },
          { path: "products", element: <ProductsPage /> },
          { path: "products/:productId", element: <ProductEditPage /> },
          { path: "company", element: <CompanySettingsPage /> },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export default function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
